import { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';

/**
 * Beats of the end-of-flow celebration, in order. The guide radiates for `PULSE_MS`,
 * `CONFETTI_VOLLEYS` volleys go off `CONFETTI_INTERVAL_MS` apart, the last one is left
 * to fall for `CONFETTI_HOLD_MS`, and then the panel fades out over `EXIT_MS` before
 * `onFinish` runs.
 */
export enum CelebrationPhase {
  IDLE = 'idle',
  PULSING = 'pulsing',
  CONFETTI = 'confetti',
  LEAVING = 'leaving',
  DONE = 'done',
}

export const PULSE_MS = 2000;
/** How many times the cannons go off. */
export const CONFETTI_VOLLEYS = 3;
/** Gap between volleys — spaced enough to read as three distinct shots, not one long burst. */
export const CONFETTI_INTERVAL_MS = 600;
/** Breathing room after the *last* volley, so the guide doesn't leave under falling paper. */
export const CONFETTI_HOLD_MS = 1200;
export const EXIT_MS = 500;

/** Wall time from the first volley to the moment the panel starts leaving. */
const CONFETTI_PHASE_MS = (CONFETTI_VOLLEYS - 1) * CONFETTI_INTERVAL_MS + CONFETTI_HOLD_MS;

/**
 * One volley: two bursts angled in from the bottom corners, so the paper crosses the
 * whole viewport instead of only the narrow side panel the guide lives in.
 */
const fireConfetti = () => {
  const base = {
    particleCount: 90,
    startVelocity: 55,
    spread: 60,
    disableForReducedMotion: true,
  };

  confetti({ ...base, angle: 60, origin: { x: 0, y: 1 } });
  confetti({ ...base, angle: 120, origin: { x: 1, y: 1 } });
};

interface Params {
  /** True once every task in the guide is checked. */
  allTasksCompleted: boolean;
  /** Runs once the panel has finished fading out — persist the dismissal here. */
  onFinish: () => void;
}

interface CelebrationState {
  phase: CelebrationPhase;
  /** The celebration has begun: swap the heading, stop offering to dismiss the guide. */
  hasStarted: boolean;
  /** Radiate the progress bar. Kept on through the confetti so it doesn't cut out mid-party. */
  isPulsing: boolean;
  /** The panel is on its way out (fading, or already handed over to `onFinish`). */
  isLeaving: boolean;
}

/**
 * Drives the celebration that plays when a teacher checks off the last task of the
 * first-month guide, then hands control back so the caller can retire the guide.
 *
 * It runs at most once per mount, guarded by a ref rather than by `phase`: depending on
 * the phase would make the effect re-run on its own state change and its cleanup would
 * clear the very timers it just scheduled.
 */
export const useFirstMonthGuideCelebration = ({ allTasksCompleted, onFinish }: Params): CelebrationState => {
  const [phase, setPhase] = useState<CelebrationPhase>(CelebrationPhase.IDLE);
  const hasStartedRef = useRef(false);
  // Read at fire time so a fresh callback identity doesn't need to restart the sequence.
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    if (!allTasksCompleted || hasStartedRef.current) return;
    hasStartedRef.current = true;

    setPhase(CelebrationPhase.PULSING);

    const timers = [
      // One timer per volley rather than an interval, so the cleanup below cancels every
      // pending shot instead of having to also guard a running interval.
      ...Array.from({ length: CONFETTI_VOLLEYS }, (_, volley) =>
        window.setTimeout(
          () => {
            fireConfetti();
            if (volley === 0) setPhase(CelebrationPhase.CONFETTI);
          },
          PULSE_MS + volley * CONFETTI_INTERVAL_MS,
        ),
      ),
      window.setTimeout(() => setPhase(CelebrationPhase.LEAVING), PULSE_MS + CONFETTI_PHASE_MS),
      window.setTimeout(
        () => {
          setPhase(CelebrationPhase.DONE);
          onFinishRef.current();
        },
        PULSE_MS + CONFETTI_PHASE_MS + EXIT_MS,
      ),
    ];

    // Unmounting mid-celebration abandons it, including the dismissal — the guide is
    // still unfinished as far as the backend knows, so it replays on the next visit.
    return () => timers.forEach(window.clearTimeout);
  }, [allTasksCompleted]);

  return {
    phase,
    hasStarted: phase !== CelebrationPhase.IDLE,
    isPulsing: phase === CelebrationPhase.PULSING || phase === CelebrationPhase.CONFETTI,
    isLeaving: phase === CelebrationPhase.LEAVING || phase === CelebrationPhase.DONE,
  };
};
